/**
 * ToastNotification — card-style toast item (Themis-styled).
 *
 * Renders: 20px type-coloured icon, body (title + message), and a
 * small close button on the right. The 4px left border carries the
 * variant colour; the card body stays neutral so the text reads
 * clearly in both modes.
 *
 * No portal here — `ToastContainer` owns the single portal and this
 * component is mounted as a child of the fixed wrapper.
 */

import React from 'react';
import styles from './ToastNotification.module.css';
import { usePlatform } from '../../../contexts/PlatformContext';

const SuccessIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <path d="M7 12.5 L10.5 16 L17 9" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <path d="M9 9 L15 15 M15 9 L9 15" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 L22 20 H2 Z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round" />
    <path d="M12 10 V14 M12 17 V17.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <path d="M12 11 V17 M12 7.5 V7.6" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M5 5 L15 15 M15 5 L5 15" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

const ICON_FOR_TYPE = {
  success: <SuccessIcon />,
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  info: <InfoIcon />,
};

const VARIANT_CLASS = {
  success: 'toastSuccess',
  error: 'toastError',
  warning: 'toastWarning',
  info: 'toastInfo',
};

export default function ToastNotification({ toast }) {
  const { actions } = usePlatform();
  if (!toast) return null;

  const type = toast.type || 'info';
  const variantClass = VARIANT_CLASS[type] || VARIANT_CLASS.info;
  const role = type === 'error' ? 'alert' : 'status';

  return (
    <div className={`${styles.toast} ${styles[variantClass]}`} role={role}>
      <div className={styles.iconWrap}>{ICON_FOR_TYPE[type] || ICON_FOR_TYPE.info}</div>
      <div className={styles.body}>
        {toast.title && <div className={styles.title}>{toast.title}</div>}
        {toast.message && <div className={styles.message}>{toast.message}</div>}
      </div>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={() => actions.removeNotification(toast.id)}
        aria-label="Dismiss notification"
      >
        <CloseIcon />
      </button>
    </div>
  );
}
